// ==========================================
// HelpDeskTicketModel.js
// ==========================================
export class HelpDeskTicketModel {
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "ticketId",
    "companyId",
    "userId",
    "userEmail",
    "email",
    "raisedBy",
    "issueType",
    "priority",
    "status",
    "description",
    "assetId",
    "assetName",
    "assetTag",
    "assignedToUid",
    "assignedToEmail",
    "auditEventId",
    "auditEventTitle",
     "locationCode",
    "remark",
    "autoCreated",

    "resolutionNotes",
    "resolutionCost",
    "maintenanceInvoice",
    "resolvedBy",
    "resolvedAt",
    "closedAt",

    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD   = "ticketId";
  static NAME_FIELD = "issueType";

  static RAISED_BY_VALUES = Object.freeze(["user", "auditor", "admin"]);
  static PRIORITY_VALUES  = Object.freeze(["Low", "Medium", "High", "Critical"]);
  static STATUS_VALUES    = Object.freeze(["Open", "In Progress", "Resolved", "Closed"]);

  static _newId() {
    // eslint-disable-next-line no-undef
    const c = typeof crypto !== "undefined" ? crypto : null;
    if (c && typeof c.randomUUID === "function") {
      try {
        return c.randomUUID();
      } catch {
        // fall through
      }
    }
    return `hd_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
  }

  constructor(data = {}, { isCreation = false } = {}) {
    // ── Seed every allowed key with a sane default ─────────────────────────
    for (const key of HelpDeskTicketModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "Open";
      } else if (key === "priority") {
        this.priority = "Medium";
      } else if (key === "raisedBy") {
        this.raisedBy = "user";
      } else if (key === "autoCreated") {
        this.autoCreated = false;
      } else {
        this[key] = null;
      }
    }

    if (isCreation) {
      // ── Creation branch: whitelist + defaults + audit stamping ───────────
      for (const key of Object.keys(data)) {
        if (!HelpDeskTicketModel.ALLOWED_KEYS.includes(key)) continue;
        if (key === "metadata") continue; // handled below
        if (data[key] !== undefined) this[key] = data[key];
      }

      // Keep id/ticketId in sync regardless of which one was supplied.
      const givenId = data.id || data.ticketId || HelpDeskTicketModel._newId();
      this.id       = data.id || givenId;
      this.ticketId = data.ticketId || givenId;

      // Coalesce email/userEmail so both are populated consistently.
      this.userEmail = data.userEmail || data.email || this.userEmail;
      this.email     = data.email || data.userEmail || this.email;

      // `resolutionCost` must preserve `0` (falsy-but-valid).
      if (data.resolutionCost !== undefined) {
        this.resolutionCost = data.resolutionCost;
      }

      // `autoCreated === false` is already preserved by the whitelist loop.

      const now = new Date().toISOString();
      this.metadata = {
        createdAt:   data.metadata?.createdAt   || data.createdAt   || now,
        createdBy:   data.metadata?.createdBy   || data.createdBy   || "system",
        updatedAt:   data.metadata?.updatedAt   || data.updatedAt   || now,
        updatedBy:   data.metadata?.updatedBy   || data.updatedBy   || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      // ── Update branch: allow-list only, metadata merged, legacy keys hoisted
      for (const key of Object.keys(data)) {
        if (!HelpDeskTicketModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else if (data[key] !== undefined) {
          this[key] = data[key];
        }
      }
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;

      // Keep email/userEmail in sync when only one side is updated.
      if (data.email !== undefined && data.userEmail === undefined) {
        this.userEmail = data.email;
      } else if (data.userEmail !== undefined && data.email === undefined) {
        this.email = data.userEmail;
      }
    }
  }

  isValid() {
    if (!this.companyId || !String(this.companyId).trim()) return false;
    if (!this.userId || !String(this.userId).trim()) return false;
    if (!this.issueType || !String(this.issueType).trim()) return false;
    if (!this.description || !String(this.description).trim()) return false;
    if (
      this.raisedBy &&
      !HelpDeskTicketModel.RAISED_BY_VALUES.includes(this.raisedBy)
    ) {
      return false;
    }
    if (
      this.priority &&
      !HelpDeskTicketModel.PRIORITY_VALUES.includes(this.priority)
    ) {
      return false;
    }
    if (
      this.status &&
      !HelpDeskTicketModel.STATUS_VALUES.includes(this.status)
    ) {
      return false;
    }
    return true;
  }

  toPayload() {
    const payload = {};
    for (const key of HelpDeskTicketModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of HelpDeskTicketModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}